import React, {useEffect, useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCarrot} from "@fortawesome/free-solid-svg-icons";
import {DataGrid, GridCellEditCommitParams, GridColDef} from "@mui/x-data-grid";
import "./CalculoVET.css"
import {useSelector} from "react-redux";
import {Store} from "../../../store/reducer";
import {CalculoVETService} from "../../../services/CalculoVETService";
import {useTranslation} from "react-i18next";
import {LabelLoginRequired} from "../../shared/LabelLoginRequired";
import {useStickyState} from "../../shared/UseStickyState";
import {AuthService} from "../../../services/AuthService";

function genColDef(nutrientData: NutrientData): GridColDef {
  return {
    field: nutrientData.name,
    headerName: nutrientData.title,
    sortable: false,
    editable: false,
    filterable: false,
    type: "number",
    headerAlign: "left",
    align: "center",
    hide: !nutrientData.shownByDefault,
    width: nutrientData.colWidth ?? 120,
    renderCell: params => {
      let value = params.value as number / 100;
      value = value * params.row.quantity;
      return (<span className="value-cell">{value.toFixed(nutrientData.precision)} {nutrientData.unit}</span>);
    }
  } as GridColDef
}

type NutrientData = MacroNutrientData | FiberData | MicroNutrientData;

interface BaseNutrientData {
  name: string,
  title: string,
  precision: number,
  colWidth?: number
  unit: string,
  shownByDefault: boolean
}

interface MacroNutrientData extends BaseNutrientData {
  type: "macro",
  calories: number
}

interface FiberData extends BaseNutrientData {
  type: "fiber"
}

interface MicroNutrientData extends BaseNutrientData {
  type: "micro"
}

const nutrients = [
  {shownByDefault: false, type: null, name: "umidade", title: "Umidade", precision: 1, unit: "%"},
  {shownByDefault: false, type: null, name: "energia", title: "Energia", precision: 0, unit: "kcal"},
  {
    shownByDefault: true,
    type: "macro",
    name: "carboidratos",
    title: "Carboidratos",
    precision: 0,
    unit: "g",
    calories: 4,
    colWidth: 135
  },
  {shownByDefault: true, type: "macro", name: "proteina", title: "Proteínas", precision: 1, unit: "g", calories: 4},
  {shownByDefault: true, type: "macro", name: "lipideos", title: "Lipídeos", precision: 1, unit: "g", calories: 9},
  {
    shownByDefault: true,
    type: "fiber",
    name: "fibraAlimentar",
    title: "Fibra Alimentar",
    precision: 0,
    unit: "g",
    colWidth: 150
  },
  {shownByDefault: false, type: "micro", name: "colesterol", title: "Colesterol", precision: 0, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "cinzas", title: "Cinzas", precision: 1, unit: "g"},
  {shownByDefault: false, type: "micro", name: "calcio", title: "Cálcio", precision: 0, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "magnesio", title: "Magnésio", precision: 0, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "manganes", title: "Manganês", precision: 2, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "fosforo", title: "Fósforo", precision: 0, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "ferro", title: "Ferro", precision: 1, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "sodio", title: "Sódio", precision: 0, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "potassio", title: "Potássio", precision: 0, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "cobre", title: "Cobre", precision: 2, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "zinco", title: "Zinco", precision: 2, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "retinol", title: "Retinol", precision: 0, unit: "mcg"},
  {shownByDefault: false, type: "micro", name: "re", title: "RE", precision: 0, unit: "mcg"},
  {shownByDefault: false, type: "micro", name: "rae", title: "RAE", precision: 0, unit: "mcg"},
  {shownByDefault: false, type: "micro", name: "tiamina", title: "Tiamina", precision: 2, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "riboflavina", title: "Riboflavina", precision: 2, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "piridoxina", title: "Piridoxina", precision: 2, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "niacina", title: "Niacina", precision: 2, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "vitaminaC", title: "Vitamina C", precision: 1, unit: "mg"},
  {shownByDefault: false, type: "micro", name: "saturados", title: "Saturados", precision: 1, unit: "g"},
  {shownByDefault: false, type: "micro", name: "monoinsaturados", title: "Monoinsaturados", precision: 1, unit: "g"},
  {shownByDefault: false, type: "micro", name: "poliinsaturados", title: "Poliinsaturados", precision: 1, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v0_50", title: "0,50", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v14_0", title: "14:0", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v16_0", title: "16:0", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v18_0", title: "18:0", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v20_0", title: "20:0", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v22_0", title: "22:0", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v24_0", title: "24:0", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v14_1", title: "14:1", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v16_1", title: "16:1", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v18_1", title: "18:1", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v20_1", title: "20:1", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v18_2", title: "18:2 n-6", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v18_3", title: "18:3 n-3", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v20_4", title: "20:4", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v20_5", title: "20:5", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v22_5", title: "22:5", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v22_6", title: "22:6", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v18_1t", title: "18:1t", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "v18_2t", title: "18:2t", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "triptofano", title: "Triptofano", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "treonina", title: "Treonina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "isoleucina", title: "Isoleucina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "leucina", title: "Leucina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "lisina", title: "Lisina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "metionina", title: "Metionina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "cistina", title: "Cistina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "fenilalanina", title: "Fenilalanina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "tirosina", title: "Tirosina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "valina", title: "Valina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "arginina", title: "Arginina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "histidina", title: "Histidina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "alanina", title: "Alanina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "acidoAspartico", title: "Ácido Aspártico", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "acidoGlutamico", title: "Ácido Glutâmico", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "glicina", title: "Glicina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "prolina", title: "Prolina", precision: 2, unit: "g"},
  {shownByDefault: false, type: "micro", name: "serina", title: "Serina", precision: 2, unit: "g"},

] as NutrientData[]

const columnDefs: GridColDef[] = [
  {
    field: "name",
    headerName: "Alimento",
    filterable: false,
    width: 150,
    sortable: false,
    disableColumnMenu: true
  }, {
    field: "quantity",
    headerName: "Quantidade",
    align: "center",
    sortable: false,
    disableColumnMenu: true,
    editable: true,
    filterable: false,
    type: "number",
    headerAlign: "left",
    width: 120,
    renderCell: params => {
      return (<span className="value-cell">{params.value} g</span>);
    }
  },
  ...nutrients.map(n => genColDef(n))
]

interface TableItem {
  id: number,
  name: string,
  quantity: number,
  carboidratos: number,
  proteina: number,
  lipideos: number,
  fibraAlimentar: number
}


export function CalculoVET() {
  const {loggedIn, user} = useSelector((state: Store) => state.AuthState);
  const hasPermission = useMemo(() => {
    return loggedIn && AuthService.hasPermission(user, "ketnutri");
  }, [loggedIn, user]);

  const {t} = useTranslation();

  const [query, setQuery] = useState("")
  const [results, setResults] = useState([] as Array<any>)
  const [loading, setLoading] = useState(false)
  const [tableItems, setTableItems] = useStickyState([] as Array<TableItem>, "tableItems");
  const [lastId, setLastId] = useState(0);
  const [recommendations, setRecommendations] = useStickyState({} as any, "recommendations")
  const [shownCols, setShownCols] = useStickyState({} as any, "shownCols")

  useEffect(() => {
    sessionStorage.setItem("cache", JSON.stringify(tableItems))
  }, [tableItems])

  if (!hasPermission) return <LabelLoginRequired loggedIn={loggedIn}/>;

  // useEffect(() => {
  //     setResults()
  // }, [results])

  let currentTimeout: NodeJS.Timeout | null;

  function requestItemsFromServer(query: string) {
    CalculoVETService.searchItem(query).then(res => {
      setResults(res);
    }).catch(error => {
      console.error(error);
    }).finally(() => {
      setLoading(false)
    })
  }

  function queryTextChanged(event: React.ChangeEvent<HTMLInputElement>) {
    let value = event.target.value;
    setQuery(value);

    if (currentTimeout !== null) {
      clearTimeout(currentTimeout);
      currentTimeout = null;
    }

    if (value) {
      setLoading(true);
      setResults([]);
      currentTimeout = setTimeout(() => requestItemsFromServer(value), 500);
    }
  }

  function getNextId() {
    let nextId = lastId + 1;
    setLastId(nextId);
    return nextId;
  }

  function itemClicked(id: number) {
    let food = results.find(f => f.id === id);

    if (!food) return;

    let newItem = {...food, id: getNextId(), quantity: 100} as TableItem;

    // let newItem = {
    //     id: getNextId(),
    //     name: food.name,
    //
    // } as TableItem

    setTableItems(oldList => [...oldList, newItem]);
  }

  function getIsShown(nutrient: NutrientData): boolean {
    return shownCols[nutrient.name] ?? nutrient.shownByDefault;
  }

  function getTotal(nutrient: NutrientData): number {
    return tableItems.map(i => (i as any)[nutrient.name] / 100 * i.quantity).reduce(function (a, b) {
      return a + b;
    }, 0) as number;
  }

  function renderTotal(nutrient: NutrientData) {
    return getTotal(nutrient).toFixed(nutrient.precision) + " " + nutrient.unit;
  }

  function getTotalCalories(nutrient: MacroNutrientData): number {
    return getTotal(nutrient) * nutrient.calories;
  }

  function renderTotalCalories(nutrient: MacroNutrientData) {
    return getTotalCalories(nutrient).toFixed(nutrient.precision) + " kcal";
  }

  function getShownMacroNutrients() {
    return nutrients.filter(n => n.type === "macro").filter(n => getIsShown(n)).map(n => n as MacroNutrientData)
  }

  function getShownFiberMicroNutrients() {
    return nutrients.filter(n => n.type === "fiber" || n.type === "micro").filter(n => getIsShown(n)).map(n => n as MacroNutrientData)
  }

  function getTotalCaloriesAll(): number {
    return getShownMacroNutrients().map(n => getTotalCalories(n)).reduce(function (a, b) {
      return a + b;
    }, 0) as number;
  }

  function renderTotalCaloriesAll() {
    return getTotalCaloriesAll().toFixed(2) + " kcal";
  }

  function getPercentTotal(nutrient: MacroNutrientData): number {
    return getTotalCalories(nutrient) / getTotalCaloriesAll();
  }

  function renderPercentTotal(nutrient: MacroNutrientData) {
    return (getPercentTotal(nutrient) * 100).toFixed(1) + " %";
  }

  function getRecommendationPercent(nutrient: NutrientData): number | null {
    if (nutrient.type === "macro") return null;
    let rec = 0;
    if (recommendations[nutrient.name]) rec = recommendations[nutrient.name];

    if (rec === 0) return null;
    return getTotal(nutrient) / rec;
  }

  function renderRecommendationPercent(nutrient: NutrientData) {
    let rec = getRecommendationPercent(nutrient);
    if (rec === null) return "-";
    return (rec * 100).toFixed(1) + " %";
  }

  function cellEditCommitted(params: GridCellEditCommitParams) {
    if (params.value === 0) {
      // delete if set 0
      setTableItems(prevState => prevState.filter(s => s.id !== params.id));
      return;
    }

    setTableItems(prevState => {
      return prevState.map(s => {
        if (s.id !== params.id) return s;
        return {...s, quantity: params.value as number}
      })
    })
  }

  function columnVisibilityChanged(name: string, isVisible: boolean) {
    let shown = {...shownCols}
    shown[name] = isVisible;
    setShownCols(shown);
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-sm-12 col-md-4 mb-3">
          <div className="query-result-container">
            <div className="form-group">
              <input
                className="form-control query-input"
                placeholder="Digite o nome aqui..."
                value={query}
                onChange={e => queryTextChanged(e)}
              />
            </div>
            {query ? (
              <div className="query-result-list">
                {results.length > 0 ?
                  (results.map(i => (
                    <div className="query-result"
                         key={i.id}
                         onClick={e => itemClicked(i.id)}>{i.name}</div>))) :
                  loading ?
                    (<div className="query-result loading"><FontAwesomeIcon icon={faCarrot}
                                                                            style={{
                                                                              color: "#fda000",
                                                                              marginRight: "10px"
                                                                            }}
                                                                            spin={true}/>Carregando...
                    </div>) :
                    (<div className="query-result text-muted">Item não encontrado.</div>)
                }
              </div>
            ) : null}
          </div>
        </div>
        <div className="col-sm-12 col-md-8">
          <div className="result-table">
            <DataGrid
              rows={tableItems}
              columns={columnDefs}
              autoPageSize={true}
              rowHeight={38}
              disableSelectionOnClick={true}
              onCellEditCommit={params => cellEditCommitted(params)}
              onColumnVisibilityChange={params => columnVisibilityChanged(params.field, params.isVisible)}
            />
          </div>
        </div>
        {getShownMacroNutrients().length > 0 ? (
          <div className="col-sm-12 col-md-6 col-lg-5 offset-lg-2 col-xl-4 offset-xl-4">
            <div className="result-total">
              <h5>Total de Macronutrientes</h5>

              <table className="table">
                <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Totais</th>
                  <th scope="col">Total de Calorias</th>
                  <th scope="col">% do total</th>
                </tr>
                </thead>
                <tbody>
                {getShownMacroNutrients().map(n => (
                  <tr key={n.name}>
                    <td>{n.title}</td>
                    <td className="font-monospace text-center">{renderTotal(n)}</td>
                    <td className="font-monospace text-center">{renderTotalCalories(n)}</td>
                    <td className="font-monospace text-center">{renderPercentTotal(n)}</td>
                  </tr>
                ))}
                <tr>
                  <th scope="col">VET</th>
                  <td colSpan={4}
                      className="text-center table-success font-monospace">{renderTotalCaloriesAll()}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {getShownFiberMicroNutrients().length > 0 ? (
          <div className="col-sm-12 col-md-6 col-lg-5 col-xl-4">
            <div className="result-total">
              <h5>Total de Fibras e Micronutrientes</h5>
              <table className="table">
                <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">
                    <div className="text-center">Totais</div>
                  </th>
                  <th scope="col">
                    <div className="text-center">Recomendação</div>
                  </th>
                  <th scope="col">
                    <div className="text-center">% da recom...</div>
                  </th>
                </tr>
                </thead>
                <tbody>
                {getShownFiberMicroNutrients().map(n => (
                  <tr key={n.name}>
                    <td>{n.title}</td>
                    <td className="font-monospace text-center">{renderTotal(n)}</td>
                    <td className="text-center">
                      <div className="d-inline-flex mx-auto">
                        <input
                          className="form-control recommendation-input"
                          value={recommendations[n.name]}
                          onChange={e => {
                            setRecommendations((r: any) => {
                              let ret = {...r};
                              ret[n.name] = e.target.value;
                              return ret;
                            })
                          }}/>
                          <span className="font-monospace">&nbsp;g</span>
                      </div>
                    </td>
                    <td className="font-monospace text-center">{renderRecommendationPercent(n)}</td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
